import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import { DialogActions, DialogContent, DialogTitle } from '../dialog/DialogTitle';
import { StyledPayButton } from '../../utilities/styles/checkoutPage.style';

interface Organization {
  name: string;
  contactPhone?: string;
  contactEmailAddress?: string;
  address1?: string;
}

interface OrganizationSettings {
  layoutColor: string;
  ordersAdvanceNoticeHours?: number;
  ordersAdvanceNoticeHoursPickUp?: number;
  ordersAdvanceNoticeHoursDelivery?: number;
  ordersAdvanceNoticeHoursEvent?: number;
  advancedNoticePolicyOverride?: string | null;
  additionalNoticeOverride?: string | null;
}

interface StorePolicyDialogProps {
  open: boolean;
  onClose: () => void;
  organization?: Organization;
  organizationSettings?: OrganizationSettings;
  contentHeight: any;
  classes?: any;
  orderType?: "Pickup" | "Delivery" | "Event";
  isScheduleNotFound?: boolean;
}

const StorePolicyDialog: React.FC<StorePolicyDialogProps> = ({
  open,
  onClose,
  organization,
  organizationSettings,
  contentHeight,
  classes = {},
  orderType,
  isScheduleNotFound,
}) => {
  if (!open) return null;

  const getAdvanceNoticeHours = (): number => {
  if (!organizationSettings) return 0;

  if (isScheduleNotFound) {
    return organizationSettings.ordersAdvanceNoticeHours ?? 0;
  }

  switch (orderType) {
    case "Pickup":
      return organizationSettings.ordersAdvanceNoticeHoursPickUp ?? 0;
    case "Delivery":
      return organizationSettings.ordersAdvanceNoticeHoursDelivery ?? 0;
    case "Event":
      return organizationSettings.ordersAdvanceNoticeHoursEvent ?? 0;
    default:
      return organizationSettings.ordersAdvanceNoticeHours ?? 0;
  }
 };

  const advanceNoticeHours = getAdvanceNoticeHours();
  const advancedNoticePolicyOverride = organizationSettings?.advancedNoticePolicyOverride;
  const additionalNoticeOverride = organizationSettings?.additionalNoticeOverride;

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      className={classes.paymentDialog}
      disableEscapeKeyDown={true}
      hideBackdrop={true}
      PaperProps={{
        style: {
          border: "1px solid",
          borderColor:
            organizationSettings?.layoutColor &&
            lighten(organizationSettings?.layoutColor, 0.5),
          borderRadius: "12px",
          overflowX: "hidden",
          width: "660px",
        },
      }}
    >
      <DialogTitle
        id="customized-dialog-title"
        onClose={onClose}
        className={classes.dialogTitle}
        classes={{ closeButton: classes.closeButton }}
        sx={{
          p: "0px 24px",
          "& svg": {
            width: 25,
          },
        }}
      />
      <DialogContent
        style={{ height: contentHeight }}
        sx={{ p: 0 }}
      >
        <Box>
          <Typography
            sx={{
              mt: 4,
              mb: 2,
              textAlign: "center",
              fontSize: { xs: "20px" },
              fontFamily: "Poppins, Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 500,
            }}
          >
            {organization?.name} Store Policy
          </Typography>

          <Grid
            container={true}
            spacing={2}
            direction={"column"}
            width="90%"
            m="0px auto"
          >
            <Grid item xs={12} sm={12}>
              <Grid container>
                <Grid item xs={12} sm={12} mt={2}>
                  <Typography
                    sx={{
                      fontFamily:
                        "Poppins, Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 500,
                      fontSize: { xs: "20px" },
                      color: "#000000",
                    }}
                  >
                    Advanced Notice Policy:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} mt={1}>
                  <Typography
                    sx={{
                      fontFamily:
                        "Poppins, Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                      fontSize: { xs: "17px" },
                      color: "#000000",
                    }}
                  >
                    {advancedNoticePolicyOverride || (
                      <>
                        {organization?.name} requires a minimum of{" "}
                        {advanceNoticeHours}{" "}
                        Hours notice on all orders submitted.
                      </>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12}>
              <Grid container>
                <Grid item xs={12} sm={12} mt={2}>
                  <Typography
                    sx={{
                      fontFamily:
                        "Poppins, Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 500,
                      fontSize: { xs: "20px" },
                      color: "#000000",
                    }}
                  >
                    Additional Notice:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} mt={1}>
                  <Typography
                    sx={{
                      fontFamily:
                        "Poppins, Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                      fontSize: { xs: "17px" },
                      color: "#000000",
                    }}
                  >
                    {additionalNoticeOverride || 
                      "Order times might also be affected by menu item prep times or delivery times."}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12}>
              <Grid container>
                <Grid item xs={12} sm={12} mt={2}>
                  <Typography
                    sx={{
                      fontFamily:
                        "Poppins, Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 500,
                      fontSize: { xs: "20px" },
                      color: "#000000",
                    }}
                  >
                    Store Contact Information:
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12} mt={2} mb={2}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      sx={{
                        fontFamily:
                          "Poppins, Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        fontSize: { xs: "17px" },
                        color: "#000000",
                      }}
                    >
                      Phone
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily:
                          "Poppins, Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        fontSize: { xs: "17px" },
                        color: "#000000",
                      }}
                    >
                      {organization?.contactPhone}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={12} mt={1} mb={2}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      sx={{
                        fontFamily:
                          "Poppins, Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        fontSize: { xs: "17px" },
                        color: "#000000",
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily:
                          "Poppins, Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        fontSize: { xs: "17px" },
                        color: "#000000",
                      }}
                    >
                      {organization?.contactEmailAddress}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={12} mt={1} mb={2}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      sx={{
                        fontFamily:
                          "Poppins, Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 400,
                        fontSize: { xs: "17px" },
                        color: "#000000",
                        flexGrow: 1,
                        maxWidth: "30%",
                      }}
                    >
                      Address
                    </Typography>

                    <Box
                      sx={{
                        flexGrow: 1,
                        maxWidth: "70%",
                        textAlign: "right",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily:
                            "Poppins, Roboto, Helvetica, Arial, sans-serif",
                          fontWeight: 400,
                          fontSize: { xs: "17px" },
                          color: "#000000",
                        }}
                      >
                        {organization?.address1}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            width: "100%",
            px: { xs: 0, sm: 4, md: 6, lg: 8 },
            pb: 2,
          }}
        >
          <StyledPayButton
            variant="contained"
            color="layoutColor"
            disableElevation
            sx={{
              backgroundColor: organizationSettings?.layoutColor,
              "&.MuiButton-root:hover": {
                backgroundColor: organizationSettings?.layoutColor,
              },
            }}
            onClick={onClose}
          >
            <Typography
              sx={{
                fontSize: { xs: "16px" },
                fontWeight: 600,
              }}
            >
              Close
            </Typography>
          </StyledPayButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default StorePolicyDialog;